import React from 'react'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import { 
  format,
  startOfWeek,
  startOfMonth,
  startOfDay,
  endOfDay,
  addDays,
  isSameMonth,
  isBefore,
  isSameDay,
  isToday as dateFnsIsToday,
  isWithinInterval
} from 'date-fns'
import Typography from '@mui/material/Typography'



const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  cellContainer: {
    width: props => props.width / 7,
    height: props => props.width / 7
  },
  cell: {
    width: '100%',
    height: '100%',
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.pink._50
    }
  },
  selectedCell: {
    width: '100%',
    height: '100%',
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.pink.main,
    position: 'relative'
  },
  selectedAndHighlightedCell: {
    width: '100%',
    height: '100%',
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.pink.main,
    position: 'relative'
  },
  highlightedCell: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.primary._100,
    cursor: 'pointer'
  },
  partialHighlightedCellLeft: {
    position: 'absolute',
    width: 'calc(100% / 2)',
    height: '100%',
    left: 0,
    backgroundColor: theme.palette.primary._100
  },
  partialHighlightedCellRight: {
    position: 'absolute',
    width: 'calc(100% / 2)',
    height: '100%',
    right: 0,
    backgroundColor: theme.palette.primary._100,
  },
  pastCell: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  todayCell: {
    border: '1px solid ' + theme.palette.primary._500
  },
  selectedText: {
    color: 'white'
  },
  pastText: {
    color: theme.palette.primary._300
  }
}))


const CalendarDays = props => {
  const { 
    date, 
    start,
    end,
    containerWidth,
    isRange,
    isActive,
    callback
  } = props
  const classes = useStyles({ width: containerWidth })
  const dayFormat = 'd'
  const startOfToday = startOfDay(new Date())
  const monthStart = startOfMonth(date)
  const startDate = startOfWeek(monthStart)
  const days = [
    0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0
  ]
  let day = startDate



  const selectDate = (newDate, isPastOrEmpty) => () => {
    if (isPastOrEmpty) return

    if (isRange) {
      if (start === null) {
        callback({ start: newDate, end: null })
      }
      else if (start !== null && end !== null) {
        callback({ start: newDate, end: null })
      }
      else {
        const newStart = newDate > start ? start : newDate
        const newEnd = newDate > start ? newDate : start
        callback({ start: newStart, end: newEnd })
      }
    }
    else {
      callback({ start: newDate, end: endOfDay(newDate) })
    }
  }


  function getIsSelected() {
    const isStartSelected = start ? isSameDay(day, start) : false
    const isEndSelected = end ? isSameDay(day, end) : false

    return isStartSelected || isEndSelected
  }


  function getIsBetweenSelected() {
    if (isRange) { 
      if (start === null || end === null) {
        return false
      }
      else if (isSameDay(start, end)) return false
      else {
        return isWithinInterval(day, { 
          start: startOfDay(start), 
          end: endOfDay(end) 
        })
      }
    } 
    else {
      return false
    }
  }


  function renderCell({ isPastOrEmpty, isSelected, isHighlighted, isToday, string, dayCopy }) {
    if (isPastOrEmpty) {
      const className = isToday 
        ? clsx(classes.pastCell, classes.todayCell) 
        : classes.pastCell

      return (
        <div className={ className }>
          <Typography className={ classes.pastText }>
            { string }
          </Typography>
        </div>
      )
    }



    if (!isActive) {
      const className = isToday 
              ? clsx(classes.cell, classes.todayCell) 
              : classes.cell

      return (
        <div className={ className }>
          <Typography>
            { string }
          </Typography>
        </div>
      )
    }



    if (isSelected && isHighlighted) {
      return (
        <div className={ classes.selectedAndHighlightedCell }>
          { isSameDay(dayCopy, start) 
            ? <div className={ classes.partialHighlightedCellRight } />
            : <div className={ classes.partialHighlightedCellLeft } />
          }

          <div className={ classes.selectedCell }>
            <Typography className={ classes.selectedText }>
              { string }
            </Typography>
          </div>
        </div>
      )
    } else if (isSelected) {
      const className = isToday 
        ? clsx(classes.selectedCell, classes.todayCell) 
        : classes.selectedCell

      return (
        <div className={ className }>
          <Typography className={ classes.selectedText }>
            { string }
          </Typography>
        </div>
      )
    } else if (isHighlighted) {
      const className = isToday 
        ? clsx(classes.highlightedCell, classes.todayCell) 
        : classes.highlightedCell

      return (
        <div className={ className }>
          <Typography>
            { string }
          </Typography>
        </div>
      )
    } else {
      const className = isToday 
        ? clsx(classes.cell, classes.todayCell) 
        : classes.cell

      return (
        <div className={ className }>
          <Typography>
            { string }
          </Typography>
        </div>
      )
    }
  }



  return (
    <div className={ classes.root }>
      { days.map((_, i) => {
          const string = isSameMonth(day, monthStart) ? format(day, dayFormat) : ''
          const isPastOrEmpty = isBefore(day, startOfToday) || string === ''
          const isSelected = getIsSelected()
          const isHighlighted = getIsBetweenSelected()
          const isToday = dateFnsIsToday(day)
          const dayCopy = new Date(day)

          day = addDays(day, 1)


          return (
            <div 
              key={ i } 
              className={ classes.cellContainer }
              onClick={ selectDate(dayCopy, isPastOrEmpty) }>
              { 
                renderCell({ 
                  isPastOrEmpty,
                  isSelected,
                  isHighlighted,
                  isToday,
                  string,
                  dayCopy
                }) 
              }
            </div>
          )
        })
      }
    </div>
  )
}


export default CalendarDays


